import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
  KeyRound,
  PlayCircle,
  Send,
  ShieldCheck,
} from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import { PAGE_SEO } from '../../config/seoConfig';
import scholarshipService from '../../services/scholarship.service';
import {
  ScholarshipCampaign,
  ScholarshipCandidateQuestion,
  ScholarshipExamAccessResponse,
  ScholarshipStartResponse,
  ScholarshipSubmitResponse,
} from '../../types/scholarship';

const EXAM_INSTRUCTIONS = [
  'Answer independently. Do not use notes, another device, messaging apps, or outside help.',
  'Do not switch tabs, minimize the browser, move the app to the background, copy, paste, or use the browser back/refresh buttons during the exam.',
  'If the exam page is hidden or the app is switched during the attempt, the exam will be submitted immediately as a malpractice/copying warning.',
  'The exam has 30 questions for 30 marks. Each question carries 1 mark.',
  'The passing requirement is 80%. Shortlisted students will be informed about the remaining process.',
  'The timer starts when you begin the exam and the attempt is submitted when time ends.',
  'Only one submitted attempt is allowed for each registered student.',
];

const MEMORIAL_SCHOLARSHIP_TITLE = 'N. Surya Narayana Memorial Scholarship 2026';
const MEMORIAL_EXAM_START = '2026-07-26T10:00:00.000+05:30';
const MEMORIAL_EXAM_END = '2026-07-26T23:59:59.999+05:30';

const isMemorialCampaign = (campaign?: ScholarshipCampaign | null) => {
  if (!campaign) return false;
  return [campaign.scholarshipId, campaign.slug, campaign.title]
    .filter(Boolean)
    .some((value) => /n[-.\s]*surya|surya narayana|localally memorial/i.test(String(value)));
};

const normalizeMemorialCampaign = (campaign: ScholarshipCampaign): ScholarshipCampaign => {
  if (!isMemorialCampaign(campaign)) return campaign;
  return {
    ...campaign,
    title: MEMORIAL_SCHOLARSHIP_TITLE,
    examStartDate: MEMORIAL_EXAM_START,
    examEndDate: MEMORIAL_EXAM_END,
    duration: 30,
    totalMarks: 30,
    passingPercentage: 80,
    totalQuestions: 30,
  };
};

const getErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message[0]) return String(message[0]);
  return fallback;
};

const formatDate = (value?: string) => {
  if (!value) return 'To be announced';
  return new Date(value).toLocaleString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatTime = (seconds: number) => {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${minutes.toString().padStart(2, '0')}:${rest.toString().padStart(2, '0')}`;
};

const ScholarshipExamPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const autoAccessRequested = searchParams.get('autoAccess') === '1';
  const [campaigns, setCampaigns] = useState<ScholarshipCampaign[]>([]);
  const [selectedScholarshipId, setSelectedScholarshipId] = useState(searchParams.get('scholarshipId') || '');
  const [accessForm, setAccessForm] = useState({
    registrationId: searchParams.get('registrationId') || '',
    studentPhone: searchParams.get('phone') || '',
  });
  const [accessResponse, setAccessResponse] = useState<ScholarshipExamAccessResponse | null>(null);
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [startResponse, setStartResponse] = useState<ScholarshipStartResponse | null>(null);
  const [submitResponse, setSubmitResponse] = useState<ScholarshipSubmitResponse | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [restrictionWarnings, setRestrictionWarnings] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [accessing, setAccessing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const [malpracticeNotice, setMalpracticeNotice] = useState<string | null>(null);
  const [autoAccessAttempted, setAutoAccessAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const restrictionSubmitRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    scholarshipService
      .listScholarships()
      .then((data) => {
        if (!mounted) return;
        const nextCampaigns = (Array.isArray(data) ? data : []).map(normalizeMemorialCampaign);
        setCampaigns(nextCampaigns);
        const nextScholarshipId =
          searchParams.get('scholarshipId') ||
          nextCampaigns.find(isMemorialCampaign)?.scholarshipId ||
          nextCampaigns[0]?.scholarshipId ||
          '';
        setSelectedScholarshipId(nextScholarshipId);
        if (nextScholarshipId) {
          setAccessForm((prev) => ({
            registrationId:
              searchParams.get('registrationId') ||
              prev.registrationId ||
              window.localStorage.getItem(`localally-scholarship-registration-${nextScholarshipId}`) ||
              '',
            studentPhone:
              searchParams.get('phone') ||
              prev.studentPhone ||
              window.localStorage.getItem(`localally-scholarship-phone-${nextScholarshipId}`) ||
              '',
          }));
        }
      })
      .catch((err) => {
        console.error('Failed to load scholarships', err);
        if (mounted) setError('Unable to load scholarship details right now.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [searchParams]);

  useEffect(() => {
    if (!startResponse || submitResponse) return undefined;
    const updateRemaining = () => {
      const expiresAt = new Date(startResponse.attempt.expiresAt).getTime();
      setRemainingSeconds(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [startResponse, submitResponse]);

  useEffect(() => {
    if (!accessResponse || startResponse || submitResponse) return undefined;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [accessResponse, startResponse, submitResponse]);

  useEffect(() => {
    if (!startResponse || submitResponse) return undefined;
    const blockEvent = (event: Event) => {
      event.preventDefault();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      const blockedShortcut =
        event.key === 'F12' ||
        ((event.ctrlKey || event.metaKey) && ['a', 'c', 'p', 's', 'u', 'v', 'x'].includes(event.key.toLowerCase()));
      if (blockedShortcut) event.preventDefault();
    };
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    document.addEventListener('contextmenu', blockEvent);
    document.addEventListener('copy', blockEvent);
    document.addEventListener('cut', blockEvent);
    document.addEventListener('paste', blockEvent);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('contextmenu', blockEvent);
      document.removeEventListener('copy', blockEvent);
      document.removeEventListener('cut', blockEvent);
      document.removeEventListener('paste', blockEvent);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [startResponse, submitResponse]);

  const selectedCampaign = useMemo(
    () => {
      const campaign =
        accessResponse?.campaign ||
        campaigns.find((item) => item.scholarshipId === selectedScholarshipId) ||
        campaigns.find(isMemorialCampaign) ||
        campaigns[0];
      return campaign ? normalizeMemorialCampaign(campaign) : undefined;
    },
    [accessResponse?.campaign, campaigns, selectedScholarshipId],
  );

  const accessPayload = useMemo(
    () => ({
      scholarshipId: selectedScholarshipId || selectedCampaign?.scholarshipId,
      registrationId: accessResponse?.registration.registrationId || accessForm.registrationId.trim(),
      studentPhone: accessForm.studentPhone.trim(),
    }),
    [accessForm.registrationId, accessForm.studentPhone, accessResponse?.registration.registrationId, selectedCampaign?.scholarshipId, selectedScholarshipId],
  );

  const questions = startResponse?.questions || [];
  const currentQuestion: ScholarshipCandidateQuestion | undefined = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const examProgressPercent = questions.length ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const examStartMs = selectedCampaign ? new Date(selectedCampaign.examStartDate).getTime() : 0;
  const examEndMs = selectedCampaign ? new Date(selectedCampaign.examEndDate).getTime() : 0;
  const canStartExamNow = Boolean(selectedCampaign && examStartMs <= nowMs && examEndMs >= nowMs);
  const examAvailabilityMessage =
    selectedCampaign && examStartMs > nowMs
      ? `The scholarship exam opens at ${formatDate(selectedCampaign.examStartDate)}.`
      : selectedCampaign && examEndMs < nowMs
        ? 'The scholarship exam window has closed.'
        : '';
  const startButtonLabel = starting
    ? 'Starting...'
    : canStartExamNow
      ? 'Start exam'
      : selectedCampaign && examEndMs < nowMs
        ? 'Exam window closed'
        : 'Exam not open yet';

  const renderWithSeo = (content: React.ReactElement) => (
    <>
      <SEO
        title={PAGE_SEO.SCHOLARSHIP_EXAM.title}
        description={PAGE_SEO.SCHOLARSHIP_EXAM.description}
        path={PAGE_SEO.SCHOLARSHIP_EXAM.path}
        robots="noindex, nofollow"
      />
      {content}
    </>
  );

  const renderPage = (content: React.ReactElement, maxWidthClass = 'max-w-5xl') =>
    renderWithSeo(
      <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#ecfeff_48%,_#fff7ed_100%)] text-slate-950">
        <section className={`mx-auto ${maxWidthClass} px-3 py-4 sm:px-6 sm:py-8 lg:px-8 lg:py-10`}>
          {content}
        </section>
      </div>,
    );

  const verifyAccess = useCallback(async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!accessPayload.registrationId || !accessPayload.studentPhone) {
      setError('Enter the registered phone number and hall ticket number.');
      return;
    }

    try {
      setAccessing(true);
      setError(null);
      setSubmitResponse(null);
      setStartResponse(null);
      setAcceptedRules(false);
      setMalpracticeNotice(null);
      restrictionSubmitRef.current = false;
      const response = await scholarshipService.accessExam(accessPayload);
      const normalizedResponse = {
        ...response,
        campaign: normalizeMemorialCampaign(response.campaign),
      };
      setAccessResponse(normalizedResponse);
      setSelectedScholarshipId(normalizedResponse.campaign.scholarshipId);
      setAccessForm((prev) => ({
        ...prev,
        registrationId: normalizedResponse.registration.registrationId,
      }));
      window.localStorage.setItem(
        `localally-scholarship-registration-${normalizedResponse.campaign.scholarshipId}`,
        normalizedResponse.registration.registrationId,
      );
      window.localStorage.setItem(
        `localally-scholarship-phone-${normalizedResponse.campaign.scholarshipId}`,
        accessPayload.studentPhone,
      );
    } catch (err: any) {
      setError(getErrorMessage(err, 'Unable to verify scholarship exam access.'));
    } finally {
      setAccessing(false);
    }
  }, [accessPayload]);

  useEffect(() => {
    if (
      loading ||
      !autoAccessRequested ||
      autoAccessAttempted ||
      accessResponse ||
      startResponse ||
      submitResponse ||
      !accessPayload.registrationId ||
      !accessPayload.studentPhone
    ) {
      return;
    }
    setAutoAccessAttempted(true);
    void verifyAccess();
  }, [
    accessPayload.registrationId,
    accessPayload.studentPhone,
    accessResponse,
    autoAccessAttempted,
    autoAccessRequested,
    loading,
    startResponse,
    submitResponse,
    verifyAccess,
  ]);

  const startExam = async () => {
    if (!canStartExamNow) {
      setError(examAvailabilityMessage || 'Scholarship exam is not available right now.');
      return;
    }

    try {
      setStarting(true);
      setError(null);
      setSubmitResponse(null);
      setAnswers({});
      setCurrentIndex(0);
      setRestrictionWarnings(0);
      setMalpracticeNotice(null);
      restrictionSubmitRef.current = false;
      const response = await scholarshipService.startExamWithAccess(accessPayload);
      const normalizedResponse = {
        ...response,
        campaign: normalizeMemorialCampaign(response.campaign),
      };
      setStartResponse(normalizedResponse);
      setAccessResponse({
        campaign: normalizedResponse.campaign,
        registration: normalizedResponse.registration,
      });
      setSelectedScholarshipId(normalizedResponse.campaign.scholarshipId);
      setAccessForm((prev) => ({
        ...prev,
        registrationId: normalizedResponse.registration.registrationId,
      }));
    } catch (err: any) {
      setError(getErrorMessage(err, 'Unable to start scholarship exam.'));
    } finally {
      setStarting(false);
    }
  };

  const submitExam = useCallback(async (restrictionReason?: string) => {
    if (!startResponse || submitting || submitResponse) return;
    try {
      setSubmitting(true);
      setError(restrictionReason || null);
      if (restrictionReason) setMalpracticeNotice(restrictionReason);
      const startedAt = new Date(startResponse.attempt.startedAt).getTime();
      const response = await scholarshipService.submitExamWithAccess({
        attemptId: startResponse.attempt.attemptId,
        registrationId: accessPayload.registrationId,
        studentPhone: accessPayload.studentPhone,
        totalTimeSpent: Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
        answers: startResponse.questions.map((question) => ({
          questionId: question.questionId,
          selectedOptionId: answers[question.questionId],
        })),
      });
      const normalizedResponse = {
        ...response,
        campaign: normalizeMemorialCampaign(response.campaign),
      };
      setSubmitResponse(normalizedResponse);
      setStartResponse((prev) => (prev ? { ...prev, attempt: normalizedResponse.attempt } : prev));
    } catch (err: any) {
      setError(getErrorMessage(err, 'Unable to submit scholarship exam.'));
    } finally {
      setSubmitting(false);
    }
  }, [accessPayload.registrationId, accessPayload.studentPhone, answers, startResponse, submitResponse, submitting]);

  const submitForRestriction = useCallback((reason: string) => {
    if (!startResponse || submitResponse || submitting || restrictionSubmitRef.current) return;
    restrictionSubmitRef.current = true;
    setRestrictionWarnings((prev) => prev + 1);
    setMalpracticeNotice(reason);
    void submitExam(reason);
  }, [startResponse, submitExam, submitResponse, submitting]);

  useEffect(() => {
    if (!startResponse || submitResponse) return undefined;
    const reason =
      'Possible malpractice/copying attempt detected. The exam was submitted automatically because the exam page was hidden or switched.';

    const handleVisibilityChange = () => {
      if (document.hidden) submitForRestriction(reason);
    };
    const handlePageHide = () => {
      submitForRestriction(reason);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [startResponse, submitForRestriction, submitResponse]);

  useEffect(() => {
    if (remainingSeconds !== 0 || !startResponse || submitResponse || submitting || autoSubmitted) return;
    setAutoSubmitted(true);
    void submitExam();
  }, [autoSubmitted, remainingSeconds, startResponse, submitExam, submitResponse, submitting]);

  if (loading) {
    return renderPage(
      <div className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm sm:p-6">
        Loading scholarship exam...
      </div>,
    );
  }

  if (submitResponse) {
    return renderPage(
      <div className="space-y-4 sm:space-y-6">
        <Link to="/scholarship" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950">
          <ArrowLeft className="h-4 w-4" />
          Scholarship
        </Link>
        <section className="-mx-3 rounded-none border-y border-emerald-200 bg-emerald-50 p-5 sm:mx-0 sm:rounded-lg sm:border sm:p-6">
          <CheckCircle2 className="h-10 w-10 text-emerald-700" />
          <h1 className="mt-4 text-2xl font-semibold text-emerald-950 sm:text-3xl">Exam submitted</h1>
          <p className="mt-2 text-sm text-emerald-800">
            Score: {submitResponse.result.totalScore} / {submitResponse.campaign.totalMarks} ({submitResponse.result.percentageScore}%)
          </p>
          {malpracticeNotice ? (
            <div className="mt-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
              <p>{malpracticeNotice}</p>
            </div>
          ) : null}
          <p className="mt-2 text-sm leading-6 text-emerald-800">
            Shortlisted students will be informed about the remaining scholarship process.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/scholarship/results"
              className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 sm:w-auto"
            >
              View results
            </Link>
            <Link
              to="/scholarship"
              className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 sm:w-auto"
            >
              Back to scholarship
            </Link>
          </div>
        </section>
      </div>,
    );
  }

  if (startResponse && currentQuestion) {
    return renderPage(
      <div className="space-y-4 pb-24 sm:space-y-5 sm:pb-0">
        <div className="sticky top-[72px] z-30 -mx-3 border-b border-slate-200 bg-white/95 px-3 py-3 shadow-sm backdrop-blur sm:static sm:mx-0 sm:rounded-lg sm:border sm:p-5 lg:flex lg:items-center lg:justify-between">
          <div className="flex items-start justify-between gap-3 lg:flex-1">
            <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700">Scholarship exam</p>
            <h1 className="mt-1 text-lg font-semibold leading-6 text-slate-950 sm:text-2xl sm:leading-8">{startResponse.campaign.title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Question {currentIndex + 1} of {questions.length} | Answered {answeredCount}
            </p>
            </div>
            <div className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 font-mono text-lg font-semibold sm:px-4 sm:py-3 sm:text-xl ${
              (remainingSeconds || 0) <= 300 ? 'bg-amber-50 text-amber-800' : 'bg-cyan-50 text-cyan-800'
            }`}>
              <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
              {formatTime(remainingSeconds ?? 0)}
            </div>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100 lg:mt-4">
            <div
              className="h-full rounded-full bg-cyan-600 transition-all duration-300"
              style={{ width: `${examProgressPercent}%` }}
            />
          </div>
        </div>

        {restrictionWarnings > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Exam restriction warning recorded: browser/tab switch detected {restrictionWarnings} time{restrictionWarnings === 1 ? '' : 's'}.
          </div>
        )}

        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

        <div className="-mx-3 overflow-x-auto px-3 pb-1 lg:hidden">
          <div className="flex min-w-max gap-2">
            {questions.map((question, index) => {
              const active = index === currentIndex;
              const answered = Boolean(answers[question.questionId]);
              return (
                <button
                  key={question.questionId}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Question ${index + 1}${answered ? ', answered' : ''}`}
                  className={`flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-semibold transition ${
                    active
                      ? 'bg-slate-950 text-white shadow-sm'
                      : answered
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200'
                  }`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_260px]">
          <section className="-mx-3 rounded-none border-y border-slate-200 bg-white p-4 shadow-sm sm:mx-0 sm:rounded-lg sm:border sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {currentQuestion.difficulty}
              </span>
              <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
                {currentQuestion.marks} mark
              </span>
            </div>
            <h2 className="mt-5 text-lg font-semibold leading-7 text-slate-950 sm:text-xl sm:leading-8">{currentQuestion.questionText}</h2>
            <div className="mt-5 space-y-3 sm:mt-6">
              {currentQuestion.options.map((option) => {
                const optionId = String(option.optionId || '').trim();
                const selected = Boolean(optionId) && answers[currentQuestion.questionId] === optionId;
                return (
                  <button
                    key={optionId || option.text}
                    type="button"
                    onClick={() => {
                      if (!optionId) return;
                      setAnswers((prev) => ({
                        ...prev,
                        [currentQuestion.questionId]: optionId,
                      }));
                    }}
                    className={`flex min-h-[56px] w-full items-start gap-3 rounded-lg border px-4 py-4 text-left transition ${
                      selected
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-950'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`mt-0.5 h-4 w-4 rounded-full border ${selected ? 'border-cyan-600 bg-cyan-600' : 'border-slate-300'}`} />
                    <span className="text-sm font-medium leading-6 sm:text-base">{option.text}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-8 hidden flex-wrap items-center gap-3 border-t border-slate-200 pt-5 sm:flex">
              <button
                type="button"
                onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentIndex === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                disabled={currentIndex >= questions.length - 1}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Submit scholarship exam now?')) void submitExam();
                }}
                disabled={submitting}
                className="ml-auto inline-flex items-center gap-2 rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </section>

          <aside className="hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:block lg:sticky lg:top-28">
            <p className="text-sm font-semibold text-slate-950">Questions</p>
            <div className="mt-4 grid grid-cols-5 gap-2">
              {questions.map((question, index) => {
                const active = index === currentIndex;
                const answered = Boolean(answers[question.questionId]);
                return (
                  <button
                    key={question.questionId}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={`aspect-square rounded-lg text-sm font-semibold transition ${
                      active
                        ? 'bg-slate-950 text-white'
                        : answered
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>
          </aside>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-16px_40px_-28px_rgba(15,23,42,0.9)] backdrop-blur sm:hidden">
          <div className="mx-auto grid max-w-md grid-cols-[48px_minmax(0,1fr)_48px] gap-2">
            <button
              type="button"
              onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              aria-label="Previous question"
              className="inline-flex h-12 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-45"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Submit scholarship exam now?')) void submitExam();
              }}
              disabled={submitting}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={() => setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1))}
              disabled={currentIndex >= questions.length - 1}
              aria-label="Next question"
              className="inline-flex h-12 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-45"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>,
      'max-w-7xl',
    );
  }

  if (accessResponse) {
    return renderPage(
      <div className="space-y-4 sm:space-y-6">
        <Link to="/scholarship" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950">
          <ArrowLeft className="h-4 w-4" />
          Scholarship
        </Link>

        <section className="-mx-3 rounded-none border-y border-slate-200 bg-white p-4 shadow-sm sm:mx-0 sm:rounded-lg sm:border sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700">Exam dashboard</p>
              <h1 className="mt-2 text-xl font-semibold leading-7 text-slate-950 sm:text-2xl sm:leading-8">{accessResponse.campaign.title}</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                <span className="block font-medium text-slate-700">{accessResponse.registration.studentName}</span>
                <span className="block">Hall ticket number: {accessResponse.registration.registrationId}</span>
              </p>
            </div>
            <div className="rounded-lg border border-cyan-100 bg-cyan-50 px-4 py-3 text-sm font-semibold leading-6 text-cyan-900 lg:max-w-xs">
              Opens: {formatDate(accessResponse.campaign.examStartDate)}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Questions</p>
              <p className="mt-2 text-xl font-semibold text-slate-950 sm:text-2xl">{accessResponse.campaign.totalQuestions}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Marks</p>
              <p className="mt-2 text-xl font-semibold text-slate-950 sm:text-2xl">{accessResponse.campaign.totalMarks}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Pass mark</p>
              <p className="mt-2 text-xl font-semibold text-slate-950 sm:text-2xl">{accessResponse.campaign.passingPercentage}%</p>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center gap-2 text-amber-900">
              <ShieldCheck className="h-5 w-5 flex-none" />
              <h2 className="text-base font-semibold">Instructions and restrictions</h2>
            </div>
            <ul className="mt-4 space-y-2 text-sm leading-6 text-amber-900">
              {EXAM_INSTRUCTIONS.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-amber-600" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {error && <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

          <label className="mt-6 flex items-start gap-3 text-sm leading-6 text-slate-700">
            <input
              type="checkbox"
              checked={acceptedRules}
              onChange={(event) => setAcceptedRules(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-700 focus:ring-cyan-600"
            />
            I understand the exam rules and will write the scholarship exam honestly.
          </label>

          {examAvailabilityMessage && (
            <div className="mt-5 rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm leading-6 text-cyan-900">
              {examAvailabilityMessage}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={startExam}
              disabled={starting || !acceptedRules || !canStartExamNow}
              className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-5 py-3 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <PlayCircle className="h-4 w-4" />
              {startButtonLabel}
            </button>
            <button
              type="button"
              onClick={() => {
                setAccessResponse(null);
                setAcceptedRules(false);
              }}
              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
            >
              Change access details
            </button>
          </div>
        </section>
      </div>,
    );
  }

  return renderPage(
    <div className="space-y-4 sm:space-y-6">
      <Link to="/scholarship" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950">
        <ArrowLeft className="h-4 w-4" />
        Scholarship
      </Link>

      <section className="-mx-3 rounded-none border-y border-slate-200 bg-white p-4 shadow-sm sm:mx-0 sm:rounded-lg sm:border sm:p-6">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="rounded-lg bg-cyan-50 p-3 text-cyan-700">
            <KeyRound className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-950 sm:text-2xl sm:leading-8">Scholarship Exam Access</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Enter the phone number and hall ticket number generated during scholarship registration.
            </p>
          </div>
        </div>

        {selectedCampaign && (
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Exam opens</p>
              <p className="mt-2 text-sm font-semibold text-slate-950">{formatDate(selectedCampaign.examStartDate)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Format</p>
              <p className="mt-2 text-sm font-semibold text-slate-950">
                {selectedCampaign.totalQuestions} questions | {selectedCampaign.totalMarks} marks
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Pass mark</p>
              <p className="mt-2 text-sm font-semibold text-slate-950">{selectedCampaign.passingPercentage}%</p>
            </div>
          </div>
        )}

        <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={verifyAccess}>
          {campaigns.length > 1 && (
            <label className="text-sm font-medium text-slate-700 md:col-span-2">
              Scholarship
              <select
                value={selectedScholarshipId}
                onChange={(event) => {
                  const nextId = event.target.value;
                  setSelectedScholarshipId(nextId);
                  setAccessForm((prev) => ({
                    registrationId: window.localStorage.getItem(`localally-scholarship-registration-${nextId}`) || prev.registrationId,
                    studentPhone: window.localStorage.getItem(`localally-scholarship-phone-${nextId}`) || prev.studentPhone,
                  }));
                }}
                className="mt-2 min-h-[44px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
              >
                {campaigns.map((campaign) => (
                  <option key={campaign.scholarshipId} value={campaign.scholarshipId}>
                    {normalizeMemorialCampaign(campaign).title}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="text-sm font-medium text-slate-700">
            Registered phone number
            <input
              value={accessForm.studentPhone}
              onChange={(event) => setAccessForm((prev) => ({ ...prev, studentPhone: event.target.value }))}
              required
              inputMode="tel"
              className="mt-2 min-h-[44px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
            />
          </label>

          <label className="text-sm font-medium text-slate-700">
            Hall ticket number
            <input
              value={accessForm.registrationId}
              onChange={(event) => setAccessForm((prev) => ({ ...prev, registrationId: event.target.value }))}
              required
              className="mt-2 min-h-[44px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
            />
          </label>

          {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 md:col-span-2">{error}</div>}

          <div className="flex flex-wrap gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={accessing}
              className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-5 py-3 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <ShieldCheck className="h-4 w-4" />
              {accessing ? 'Verifying...' : 'Open exam dashboard'}
            </button>
            <Link
              to="/scholarship"
              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
            >
              Register first
            </Link>
          </div>
        </form>

        <div className="mt-6 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
          <p>
            This access is only for the scholarship exam and is different from LocalAlly app login.
          </p>
        </div>
      </section>
    </div>,
  );
};

export default ScholarshipExamPage;
